import { useState } from 'react';
import Cross from '../../../icons/Cross';
import colorConfigs from '../../../configs/colorConfigs';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CardNumberValidate from '../../../components/CardNumberValidate';
import ExpiryDateValidator from '../../../components/ExpiryDateValidator';
import CvvValidate from '../../../components/CvvValidate';
import { useTheme } from '../../../components/hooks/useTheme';
import { Button, Dialog } from '@mui/material';

type UpgradePlanProps = {
    showPopupForPayment: boolean;
    setShowPopupForPayment: (value: boolean) => void;
    upgradPlan: any;
    isProcessing: boolean;
    setIsProcessing: (value: boolean) => void;
    callTheAPIForUpgradeSubscription: (upgradedPlan: any, paymentDetails: any, cb: any) => void;
    paymentType: number;
    setIsRefresh: (value: boolean) => void;
}

const UpgradePlan = ({
    showPopupForPayment,
    setShowPopupForPayment,
    upgradPlan,
    isProcessing,
    setIsProcessing,
    callTheAPIForUpgradeSubscription,
    paymentType,
    setIsRefresh
}: UpgradePlanProps) => {
    const { themeStyles } = useTheme();
    const [creditCardDetails, setCreditCardDetails] = useState<any>({
        cardholderName: "",
        creditCardNumber: "",
        expirationDate: "",
        ccvCode: "",
        ccLastFourDigit: ""
    });

    const [achDetails, setACHDetails] = useState<any>({
        accountNumber: "",
        accountType: "",
        routingNumber: "",
        bankName: "",
        bankPhone: "",
        accountHolderName: "",
        useAccountInfoInFuture: false,
        signedAcknowledgement: false
    });

    const closeModal = () => setShowPopupForPayment(false);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isProcessing) {
            setIsProcessing(true);

            const paymentDetails = paymentType === 0 ? creditCardDetails : achDetails;

            callTheAPIForUpgradeSubscription(upgradPlan, paymentDetails, () => {
                if (paymentType === 0) {
                    setCreditCardDetails({
                        cardholderName: "",
                        creditCardNumber: "",
                        expirationDate: "",
                        ccvCode: ""
                    });
                } else {
                    setACHDetails({
                        accountNumber: "",
                        accountType: "",
                        routingNumber: "",
                        bankName: "",
                        bankPhone: "",
                        accountHolderName: "",
                        useAccountInfoInFuture: false,
                        signedAcknowledgement: false
                    });
                }

                setIsRefresh(true);
                closeModal();
            });
        }
    }

    return (
        <Dialog
            open={showPopupForPayment}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '800px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div className={`fixed inset-0 p-4 mt-16 ${showPopupForPayment ? 'flex' : 'hidden'} flex-wrap justify-center items-center w-full h-fit z-50 overflow-auto`}>
                <div className={`w-full max-w-[32rem] shadow-lg rounded-lg relative ${themeStyles.main.tailwindMainBg}`}>
                    <div className={`text-white bg-[#167AC3] px-4 py-2 w-full h-[40px] flex justify-between items-center rounded-t-lg`}>
                        <h4 className='font-medium'>Payment</h4>
                        {!isProcessing && <Cross closeModal={closeModal} />}
                    </div>

                    <div className="w-full mt-5 p-2.5">
                        <form
                            className="mt-1 flex flex-col gap-4"
                            onSubmit={handleSubmit}
                        >
                            {
                                paymentType === 0
                                    ? (
                                        <>
                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor} text-left`}>Cardholder Name</label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={creditCardDetails.cardholderName}
                                                    onChange={(e) =>
                                                        setCreditCardDetails({
                                                            ...creditCardDetails,
                                                            cardholderName: e.target.value,
                                                        })
                                                    }
                                                    required
                                                />
                                            </div>

                                            <div className="relative flex flex-col">
                                                <CardNumberValidate
                                                    value={creditCardDetails.creditCardNumber}
                                                    setValue={(value) =>
                                                        setCreditCardDetails({
                                                            ...creditCardDetails,
                                                            creditCardNumber: value,
                                                        })
                                                    }
                                                />
                                                <CreditCardIcon
                                                    style={{
                                                        width: "18px",
                                                        height: "18px",
                                                        position: "absolute",
                                                        right: "1rem",
                                                        top: "70%",
                                                        transform: "translateY(-50%)",
                                                        color: "#bbbbbb",
                                                    }}
                                                />
                                            </div>

                                            <div className="flex justify-between items-center gap-2">
                                                <div className="relative flex-1 flex flex-col">
                                                    <ExpiryDateValidator
                                                        expiryDate={creditCardDetails.expirationDate}
                                                        setExpiryDate={(value) =>
                                                            setCreditCardDetails({
                                                                ...creditCardDetails,
                                                                expirationDate: value,
                                                            })
                                                        }
                                                    />
                                                </div>

                                                <div className="relative flex-1 flex flex-col">
                                                    <CvvValidate
                                                        value={creditCardDetails.ccvCode}
                                                        setValue={(value) =>
                                                            setCreditCardDetails({
                                                                ...creditCardDetails,
                                                                ccvCode: value,
                                                            })
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Account Holder Name
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={achDetails.accountHolderName}
                                                    onChange={(e) =>
                                                        setACHDetails({ ...achDetails, accountHolderName: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Account Number
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={achDetails.accountNumber}
                                                    onChange={(e) =>
                                                        setACHDetails({ ...achDetails, accountNumber: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Routing Number
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={achDetails.routingNumber}
                                                    onChange={(e) =>
                                                        setACHDetails({ ...achDetails, routingNumber: e.target.value })
                                                    }
                                                    required
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Bank Name
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={achDetails.bankName}
                                                    onChange={(e) =>
                                                        setACHDetails({ ...achDetails, bankName: e.target.value })
                                                    }
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Bank Phone
                                                </label>
                                                <input
                                                    type="text"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={achDetails.bankPhone}
                                                    onChange={(e) =>
                                                        setACHDetails({ ...achDetails, bankPhone: e.target.value })
                                                    }
                                                />
                                            </div>

                                            <div className="flex flex-col">
                                                <label className={`block text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Account Type
                                                </label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={achDetails.accountType}
                                                    onChange={(e) =>
                                                        setACHDetails({ ...achDetails, accountType: e.target.value })
                                                    }
                                                    required
                                                >
                                                    <option value="">Select</option>
                                                    <option value="checking">Checking</option>
                                                    <option value="savings">Savings</option>
                                                </select>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="useAccountInfoInFuture"
                                                    checked={achDetails.useAccountInfoInFuture}
                                                    onChange={(e) =>
                                                        setACHDetails({
                                                            ...achDetails,
                                                            useAccountInfoInFuture: e.target.checked
                                                        })
                                                    }
                                                />
                                                <label htmlFor="useAccountInfoInFuture" className={`text-xs ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    Use account info in future
                                                </label>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="signedAcknowledgement"
                                                    checked={achDetails.signedAcknowledgement}
                                                    onChange={(e) =>
                                                        setACHDetails({
                                                            ...achDetails,
                                                            signedAcknowledgement: e.target.checked
                                                        })
                                                    }
                                                    required
                                                />
                                                <label htmlFor="signedAcknowledgement" className={`text-xs ${themeStyles.form.tailwindLabelTextColor}`}>
                                                    I acknowledge and authorize ACH transactions
                                                </label>
                                            </div>
                                        </>
                                    )
                            }

                            <div className='pt-4'>
                                <Button variant="contained"
                                    sx={{
                                        backgroundColor: colorConfigs.btn.main.bg
                                    }}
                                    type="submit"
                                >
                                    {isProcessing ? "Processing..." : "Save"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </Dialog>
    );
}

export default UpgradePlan;